import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Drawer } from '@opengov/components-drawer';
import { Magnify } from '@opengov/react-capital-assets';
import { useMemo, useState } from 'react';

import type { TcrResource } from '../../types/vcm';

type StatusChipColor = 'success' | 'error' | 'warning' | 'default';

const STATUS_COLOR: Record<string, StatusChipColor> = {
  Active: 'success',
  Inactive: 'error',
  'Under Review': 'warning',
};

const STATUS_OPTIONS = ['All statuses', 'Active', 'Inactive', 'Under Review'] as const;
const GEO_OPTIONS = ['All geographic areas', 'Statewide', 'Regional', 'County-specific'] as const;

export interface ResourcePickerDrawerProps {
  open: boolean;
  onClose: () => void;
  resources: TcrResource[];
  onSelect: (resource: TcrResource) => void;
}

export function ResourcePickerDrawer({
  open,
  onClose,
  resources,
  onSelect,
}: ResourcePickerDrawerProps) {
  const [query, setQuery] = useState('');
  const [countyFilter, setCountyFilter] = useState('All counties');
  const [categoryFilter, setCategoryFilter] = useState('All recovery needs');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [geoFilter, setGeoFilter] = useState('All geographic areas');

  const counties = useMemo(
    () => ['All counties', ...new Set(resources.map((r) => r.county))],
    [resources],
  );
  const categories = useMemo(
    () => ['All recovery needs', ...new Set(resources.map((r) => r.category))],
    [resources],
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return resources
      .filter(
        (r) =>
          !q ||
          `${r.name} ${r.category} ${r.service} ${r.organization}`.toLowerCase().includes(q),
      )
      .filter((r) => countyFilter === 'All counties' || r.county === countyFilter)
      .filter((r) => categoryFilter === 'All recovery needs' || r.category === categoryFilter)
      .filter((r) => statusFilter === 'All statuses' || r.status === statusFilter)
      .filter((r) => geoFilter === 'All geographic areas' || r.geographicArea === geoFilter);
  }, [resources, query, countyFilter, categoryFilter, statusFilter, geoFilter]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Select a resource"
      description="Browse and filter the resource library, then select a resource to add as a referral."
      width="70%"
      anchor="right"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 2, overflow: 'hidden' }}>
        {/* Filters */}
        <Card variant="outlined" sx={{ flexShrink: 0 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField select fullWidth size="small" label="County" value={countyFilter} onChange={(e) => setCountyFilter(e.target.value)}>
                  {counties.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField select fullWidth size="small" label="Recovery need" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                  {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField select fullWidth size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField select fullWidth size="small" label="Geographic area" value={geoFilter} onChange={(e) => setGeoFilter(e.target.value)}>
                  {GEO_OPTIONS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>
              </Grid>
            </Grid>
            <Divider sx={{ my: 1.5 }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, service, category, or organization"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <Box sx={{ display: 'flex', mr: 1, color: (t) => t.palette.text.secondary, '& svg': { width: 18, height: 18 } }}>
                      <Magnify />
                    </Box>
                  ),
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Results */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary, display: 'block', mb: 1.5 }}>
            {filtered.length} resource{filtered.length !== 1 ? 's' : ''}
          </Typography>
          {filtered.length === 0 ? (
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                  No resources match your search. Adjust the filters or try a different query.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={1.5}>
              {filtered.map((resource) => (
                <Grid key={resource.id} size={{ xs: 12, sm: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                          {resource.name}
                        </Typography>
                        <Chip
                          label={resource.status}
                          size="small"
                          variant="outlined"
                          color={STATUS_COLOR[resource.status] ?? 'default'}
                          sx={{ flexShrink: 0 }}
                        />
                      </Stack>
                      <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                        {resource.service}
                      </Typography>
                      <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
                        {resource.organization}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip label={resource.category} size="small" color="info" variant="outlined" />
                        <Chip label={resource.county} size="small" variant="outlined" />
                      </Stack>
                      <Box sx={{ flex: '1 1 auto' }} />
                      <Divider />
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => { onSelect(resource); onClose(); }}
                      >
                        Select
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
